import { v4 as uuidv4 } from 'uuid';
import { getLocalStorage } from './storage';
import { getCustomData } from './customData';
import { ExperienceDetails } from 'types/ProductDetailsType';
import { getSessionIdFromCookie } from './identifierCookies';
import getMetaIntegration from './getMetaIntegration';
import { getTiktokIntegration, getGoogleIntegration } from './adIdentifiers';
// One-directional on purpose: logGtmEvent depends on the builder, never the
// reverse. RebateEventData lives with the builder so the two modules can never
// form an import cycle.
import buildRebateEventData, {
  BuildRebateEventDataArgs,
} from './buildRebateEventData';

export enum PossibleGtmEventNames {
  CONTENT_GATE_CHECKED = 'contentGateChecked',
  REGISTRATION_INITIATION = 'registrationInitiation',
  REGISTRATION_SUBMISSION = 'registrationSubmission',
  REBATE_INITIATION = 'rebateInitiation',
  REBATE_SUBMISSION = 'rebateSubmission',
}

export type RegistrationSubmissionEventData = {
  customerData: Record<string, any>;
  registrationData: Record<string, any>;
  experienceData: Record<string, any>;
};

const getCommonEventEnvelope = () => {
  const sessionId: string = getSessionIdFromCookie();
  const gdprRegion: boolean =
    Intl.DateTimeFormat().resolvedOptions().timeZone.indexOf('Europe') === 0 ||
    false;

  const customDataObject = getCustomData();
  const hasCustomData = Object.keys(customDataObject).length > 0;
  const customData = hasCustomData
    ? Object.entries(customDataObject).map(([key, value]) => ({
        key,
        value,
      }))
    : [];

  return {
    eventId: uuidv4(),
    sessionId,
    gdprRegion,
    ...(hasCustomData && { customData }),
  };
};

const pushToDataLayer = (eventName: string, payload: Record<string, any>) => {
  const w: any = window;
  if (!w?.dataLayer) return;

  const fullPayload = {
    event: `brij.${eventName}`,
    ...payload,
  };

  w.dataLayer.push(fullPayload);
};

const logGtmEvent = (eventName: PossibleGtmEventNames, user?: any) => {
  const userDateOfBirth: string =
    getLocalStorage().getItem('ageGateBirthDate') || '';
  const details = JSON.parse(
    getLocalStorage().getItem('brijProductDetails') || '{}'
  );
  const experienceDetails: ExperienceDetails =
    details?.product?.experienceDetails;

  pushToDataLayer(eventName, {
    ...getCommonEventEnvelope(),
    ...(experienceDetails && { experienceDetails }),
    user: {
      ...user,
      ...(userDateOfBirth && { dateOfBirth: userDateOfBirth }),
    },
  });
};

export const logRegistrationSubmissionGtmEvent = (
  payload: RegistrationSubmissionEventData
) => {
  pushToDataLayer(PossibleGtmEventNames.REGISTRATION_SUBMISSION, {
    ...getCommonEventEnvelope(),
    experienceData: payload.experienceData,
    customerData: payload.customerData,
    registrationData: payload.registrationData,
  });
};

// Rebate initiation / submission. The group names (experienceData,
// customerData, rebateData, rebateCampaignData) are the same four the server
// composes rebateInitiationEvent and rebateSubmissionEvent from
// (global-events-watcher/helper/getRebate{Initiation,Submission}Event.js), and
// the ad-identifier objects keep the metaIntegration / tiktokIntegration /
// googleIntegration names the rebate API payloads already use, so a dataLayer
// event and its webhook counterpart line up field for field.
//
// Ad identifiers are NOT gated on cookie consent here: no existing Brij
// dataLayer event applies consent logic, and the rebate API payloads send the
// same identifiers ungated alongside the consent snapshot. The snapshot rides
// on customerData.cookieConsent so a consumer can make its own decision.
//
// Never throws: a failure building or pushing an analytics event must not break
// a rebate opt-in or a receipt upload. The payload is BUILT inside the try too,
// not passed in already-built - every call site sits in the success branch of a
// try/catch that owns the user-visible outcome, so a throw while assembling the
// event would surface to the shopper as a failed opt-in or upload.
export const logRebateGtmEvent = (
  eventName:
    | PossibleGtmEventNames.REBATE_INITIATION
    | PossibleGtmEventNames.REBATE_SUBMISSION,
  args: BuildRebateEventDataArgs
) => {
  try {
    const payload = buildRebateEventData(args);
    pushToDataLayer(eventName, {
      ...getCommonEventEnvelope(),
      experienceData: payload.experienceData,
      customerData: payload.customerData,
      rebateData: payload.rebateData,
      ...(Object.keys(payload.rebateCampaignData).length > 0 && {
        rebateCampaignData: payload.rebateCampaignData,
      }),
      metaIntegration: getMetaIntegration(),
      tiktokIntegration: getTiktokIntegration(),
      googleIntegration: getGoogleIntegration(),
    });
  } catch (error) {
    console.error('[REBATE_GTM] Failed to push rebate event:', error);
  }
};

export default logGtmEvent;
